import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "@/components/ui/sonner";
import GlobalErrorModal from "@/components/GlobalErrorModal";
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages (route-level — lazy-loaded so each screen ships as its own small chunk
// instead of one giant bundle; dramatically speeds up first load on production)
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const SignupPayment = lazy(() => import("@/pages/SignupPayment"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const HelpAndFeedback = lazy(() => import("@/pages/HelpAndFeedback"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const BetaTesters = lazy(() => import("@/pages/BetaTesters"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const WhoWeAre = lazy(() => import("@/pages/WhoWeAre"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const ResumeCheckout = lazy(() => import("@/pages/ResumeCheckout"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const ProjectMessenger = lazy(() => import("@/pages/Project/ProjectMessenger"));
const CrewMessenger = lazy(() => import("@/pages/Project/CrewMessenger"));
const ContactManager = lazy(() => import("@/pages/Project/ContactManager"));
const Contacts = lazy(() => import("@/pages/crm/Contacts"));
const Quotes = lazy(() => import("@/pages/crm/Quotes"));
const Inbox = lazy(() => import("@/pages/crm/Inbox"));
const Reports = lazy(() => import("@/pages/crm/Reports"));
const LeadsBoard = lazy(() => import("@/pages/crm/LeadsBoard"));
const Settings = lazy(() => import("@/pages/Settings"));
const Profile = lazy(() => import("@/pages/Profile"));
const ClientPortal = lazy(() => import("@/pages/ClientPortal"));
const CrewPortal = lazy(() => import("@/pages/CrewPortal"));
// Admin Pages
const AdminLayout = lazy(() => import("@/components/layout/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminTenants = lazy(() => import("@/pages/admin/AdminTenants"));
const AdminTenantDetail = lazy(() => import("@/pages/admin/AdminTenantDetail"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminProjects = lazy(() => import("@/pages/admin/AdminProjects"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/AdminSubscriptions"));
const AdminAIUsage = lazy(() => import("@/pages/admin/AdminAIUsage"));
const AdminWebsiteAnalytics = lazy(() => import("@/pages/admin/AdminWebsiteAnalytics"));
const AdminTrainingVideo = lazy(() => import("@/pages/admin/AdminTrainingVideo"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AdminAuditLogs"));
const AdminErrorLogs = lazy(() => import("@/pages/admin/AdminErrorLogs"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/AdminEmailTemplates"));
const AdminSupportInbox = lazy(() => import("@/pages/admin/AdminSupportInbox"));
const AdminInactiveUsers = lazy(() => import("@/pages/admin/AdminInactiveUsers"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Dialer = lazy(() => import("@/pages/Dialer"));
const Voicemails = lazy(() => import("@/pages/Voicemails"));
const PublicNewsletter = lazy(() => import("@/pages/PublicNewsletter"));
const AdminNewsletters = lazy(() => import("@/pages/admin/AdminNewsletters"));
const AdminMessageCenter = lazy(() => import("@/pages/admin/AdminMessageCenter"));
const AdminNotifications = lazy(() => import("@/pages/admin/AdminNotifications"));
import { I18nProvider, useI18n } from "@/i18n";

// Components
import Sidebar from "@/components/layout/Sidebar";
import Header from "@/components/layout/Header";
import AngelaChat from "@/components/angela/AngelaChat";
import AnalyticsProvider from "@/components/analytics/AnalyticsProvider";
import { TourProvider, TourUI } from "@/components/training";
import { ConfirmProvider } from "@/components/common/ConfirmProvider";
import { VoiceCallProvider } from "@/contexts/VoiceCallProvider";
import { CallWidget } from "@/components/voice/CallWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ── Global axios interceptor: handle unusable auth tokens ──
// If the backend returns 401 for an expired/invalid token or a displaced
// single-session ("session_invalidated"), clear local state, toast the user,
// and redirect to login instead of leaving them stuck with a broken session.
let _sessionInterceptorInstalled = false;
const installSessionInterceptor = () => {
  if (_sessionInterceptorInstalled) return;
  _sessionInterceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      // Auth failures that mean the local token can no longer be used: a newer
      // login displaced this device (session_invalidated), the JWT lifetime
      // elapsed (Token expired), or the token is otherwise unusable (Invalid
      // token). In all cases clear local state and send the user to log in
      // again instead of leaving them stuck with a broken session.
      // Real system errors (server 5xx or a network/timeout failure) get a
      // reassuring global modal — these are the ones logged to admin Error Logs.
      // Validation errors (400/422) keep their own inline toasts (actionable).
      const isServerError = typeof status === "number" && status >= 500 && status <= 599;
      const isNetworkError = !error?.response && !axios.isCancel?.(error) && error?.code !== "ERR_CANCELED";
      if (isServerError || isNetworkError) {
        try {
          const backendRef = error?.response?.headers?.["x-error-reference"];
          window.dispatchEvent(new CustomEvent("arq:global-error", {
            detail: { status: status || 0, isNetwork: isNetworkError, url: error?.config?.url || "", at: Date.now(), reference: backendRef || null },
          }));
        } catch (_e) { /* ignore */ }
      }
      const AUTH_FAILURES = ["session_invalidated", "Token expired", "Invalid token"];
      if (status === 401 && AUTH_FAILURES.includes(detail)) {
        // Ignore auth-endpoint failures (e.g. wrong password on /auth/login) so
        // we don't hijack their own error handling / redirect loop.
        const reqUrl = error?.config?.url || "";
        const isAuthEndpoint = reqUrl.includes("/auth/login") || reqUrl.includes("/auth/signup");
        if (!isAuthEndpoint) {
          try {
            localStorage.removeItem("arq_token");
            localStorage.removeItem("arq_admin_token");
          } catch (_e) { /* ignore storage errors */ }
          try {
            toast.error(
              detail === "session_invalidated"
                ? "Your session was ended because you logged in from another device."
                : "Your session has expired. Please log in again."
            );
          } catch (_e) { /* ignore toast errors */ }
          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
      }
      return Promise.reject(error);
    }
  );
};
installSessionInterceptor();

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/**
 * Compute the tenant-scoped role for the logged-in user.
 * Owner: signed up the tenant (developer_user_id is unset or equals own id).
 * Manager/Staff: explicit team_role on the user doc; defaults to staff.
 */
export const useRole = () => {
  const { user } = useAuth();
  if (!user) return "staff";
  const devId = user.developer_user_id;
  if (!devId || devId === user.id) return "owner";
  return user.team_role || "staff";
};

// Sidebar Context for collapse state
const SidebarContext = createContext(null);
export const useSidebar = () => useContext(SidebarContext);

// Angela Page Context — tells Angela which page/stage the user is on
const AngelaPageContext = createContext({ page: "general", stage: null, projectId: null, projectData: null });
export const useAngelaPage = () => useContext(AngelaPageContext);
export const AngelaPageProvider = AngelaPageContext.Provider;

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("arq_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem("arq_token");
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem("arq_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("arq_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const signup = async (userData) => {
    const response = await axios.post(`${API}/auth/signup`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem("arq_token", access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = async () => {
    const savedToken = localStorage.getItem("arq_token");
    if (savedToken) {
      try {
        await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${savedToken}` } });
      } catch (_e) { /* ignore logout errors; clear locally anyway */ }
    }
    localStorage.removeItem("arq_token");
    localStorage.removeItem("arq_admin_token");
    setToken(null);
    setUser(null);
  };

  const isEmulating = !!localStorage.getItem("arq_admin_token");

  const stopEmulating = async () => {
    const adminToken = localStorage.getItem("arq_admin_token");
    if (!adminToken) return;
    localStorage.removeItem("arq_admin_token");
    localStorage.setItem("arq_token", adminToken);
    setToken(adminToken);
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setUser(res.data);
    } catch {
      localStorage.removeItem("arq_token");
      setToken(null);
      setUser(null);
    }
    window.location.href = "/admin";
  };

  const value = { user, token, loading, login, signup, logout, setUser, setToken, isEmulating, stopEmulating };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Syncs language settings from backend when user is authenticated
function LanguageSync() {
  const { token } = useAuth();
  const { updateLangs } = useI18n();
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/settings/languages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => updateLangs({
        web_app: res.data.lang_web_app || "en",
        client_portal: res.data.lang_client_portal || "en",
        crew_portal: res.data.lang_crew_portal || "en",
      }))
      .catch(() => {});
  }, [token, updateLangs]);
  return null;
}

// Protected Route (blocks super_admin — they should only use /admin, unless emulating)
const ProtectedRoute = ({ children }) => {
  const { user, token, loading, isEmulating } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f14] flex items-center justify-center">
        <div className="animate-pulse text-[#00d4ff] text-xl">Loading...</div>
      </div>
    );
  }

  if (!token) {
    // Preserve the intended destination (e.g. a /calendar deep link from a
    // newsletter) so we can land the user there after they log in.
    const dest = location.pathname + location.search;
    if (dest && dest !== "/" && !dest.startsWith("/landing")) {
      try { localStorage.setItem("arq_post_login_redirect", dest); } catch { /* ignore */ }
    }
    return <Navigate to="/landing" state={{ from: location }} replace />;
  }

  if (user?.is_super_admin && !isEmulating) {
    return <Navigate to="/admin" replace />;
  }

  // Gate unpaid accounts: an owner (or their team) whose subscription hasn't been
  // paid for is sent to the payment page to complete checkout before using the app.
  // Skipped while a super admin is impersonating so support can still inspect.
  if (user?.access_blocked && !isEmulating) {
    const plan = user?.subscription?.plan || user?.plan || "pro";
    return <Navigate to={`/signup/payment?plan_id=${plan}`} replace />;
  }

  return children;
};

// Admin Route Guard
const AdminRoute = ({ children }) => {
  const { user, token, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f14] flex items-center justify-center">
        <div className="animate-pulse text-[#00d4ff] text-xl">Loading...</div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user?.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main Layout with Sidebar and Header
const MainLayout = ({ children }) => {
  const [angelaOpen, setAngelaOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [angelaPage, setAngelaPage] = useState({ page: "general", stage: null, projectId: null, projectData: null });
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Derive page context from route
  useEffect(() => {
    const path = location.pathname;
    if (path === "/dashboard") setAngelaPage(prev => ({ ...prev, page: "dashboard" }));
    else if (path === "/projects" || path === "/projects/") setAngelaPage(prev => ({ ...prev, page: "projects" }));
    else if (path.startsWith("/projects/") && !path.includes("/messenger") && !path.includes("/crew")) {
      // ProjectDetail — stage is set by ProjectDetail via setAngelaPage
    }
    else if (path === "/settings") setAngelaPage(prev => ({ ...prev, page: "settings" }));
    else if (path === "/crew") setAngelaPage(prev => ({ ...prev, page: "crew" }));
    else if (path.startsWith("/crm/")) setAngelaPage(prev => ({ ...prev, page: path.split("/")[2] || "general" }));
    else setAngelaPage(prev => ({ ...prev, page: "general" }));
  }, [location.pathname]);

  return (
    <SidebarContext.Provider value={{ collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed, mobileMenuOpen, setMobileMenuOpen }}>
      <AngelaPageProvider value={{ ...angelaPage, setAngelaPage }}>
        <TourProvider>
          <VoiceCallProvider>
          <div className="min-h-screen bg-[#0a0f14] flex">
            <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
            <div className={`flex-1 min-w-0 flex flex-col h-dvh transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-56'}`}>
              <Header onAngelaClick={() => setAngelaOpen(!angelaOpen)} onMenuClick={() => setMobileMenuOpen(true)} />
              <main className="flex-1 min-h-0 p-3 sm:p-4 md:p-6 overflow-y-auto">
                {children}
              </main>
            </div>
            <AngelaChat isOpen={angelaOpen} onToggle={() => setAngelaOpen(!angelaOpen)} />
            <CallWidget />
            <TourUI />
          </div>
          </VoiceCallProvider>
        </TourProvider>
      </AngelaPageProvider>
    </SidebarContext.Provider>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const CatchAllRedirect = () => {
  const { user, isEmulating } = useAuth();
  return <Navigate to={(user?.is_super_admin && !isEmulating) ? "/admin" : "/"} replace />;
};

const EmulationBanner = () => {
  const { user, isEmulating, stopEmulating } = useAuth();
  if (!isEmulating) return null;
  return (
    <div className="fixed top-0 left-0 z-[100] bg-amber-500 text-black py-0.5 px-2.5 text-[10px] font-medium flex items-center gap-2 rounded-br-md" data-testid="emulation-banner">
      <span>{user?.first_name} {user?.last_name}</span>
      <button onClick={stopEmulating} data-testid="stop-emulating-btn"
        className="bg-black/20 hover:bg-black/30 text-black px-1.5 py-px rounded text-[9px] font-bold transition-colors">
        Exit
      </button>
    </div>
  );
};

const EmulationOffset = () => {
  const { isEmulating } = useAuth();
  if (!isEmulating) return null;
  return <div className="h-0" />;
};

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <LanguageSync />
        <EmulationBanner />
        <ScrollToTop />
        <AnalyticsProvider />
        <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen bg-[#0a0f14] flex items-center justify-center" data-testid="route-loading">
            <div className="animate-pulse text-[#00d4ff] text-xl">Loading...</div>
          </div>
        }>
        <Routes>
          {/* Public Routes */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/request-demo" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/who-we-are" element={<WhoWeAre />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/beta" element={<BetaTesters />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/signup/payment" element={<SignupPayment />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/resume-checkout" element={<ResumeCheckout />} />
          <Route path="/portal/:token" element={<ClientPortal />} />
          <Route path="/crew-portal/:projectId" element={<CrewPortal />} />
          <Route path="/n/:id" element={<PublicNewsletter />} />
          
          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/projects" element={
            <ProtectedRoute>
              <MainLayout>
                <Projects />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/projects/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectDetail />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/projects/:id/messenger" element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectMessenger />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/projects/:id/crew" element={
            <ProtectedRoute>
              <MainLayout>
                <CrewMessenger />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/projects/:id/contact-manager" element={
            <ProtectedRoute>
              <MainLayout>
                <ContactManager />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/crew" element={<Navigate to="/settings?tab=crew" replace />} />
          <Route path="/material-list" element={<Navigate to="/settings?tab=materials" replace />} />
          <Route path="/voicemails" element={
            <ProtectedRoute>
              <MainLayout>
                <Voicemails />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/crm/contacts" element={
            <ProtectedRoute>
              <MainLayout>
                <Contacts />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/crm/quotes" element={
            <ProtectedRoute>
              <MainLayout>
                <Quotes />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/crm/inbox" element={
            <ProtectedRoute>
              <MainLayout>
                <Inbox />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/crm/leads" element={
            <ProtectedRoute>
              <MainLayout>
                <LeadsBoard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/crm/reports" element={
            <ProtectedRoute>
              <MainLayout>
                <Reports />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/calendar" element={
            <ProtectedRoute>
              <MainLayout>
                <Calendar />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/dialer" element={
            <ProtectedRoute>
              <MainLayout>
                <Dialer />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <MainLayout>
                <Profile />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/help" element={
            <ProtectedRoute>
              <MainLayout>
                <HelpAndFeedback />
              </MainLayout>
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/tenants" element={
            <AdminRoute>
              <AdminLayout>
                <AdminTenants />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/tenants/:tenantId" element={
            <AdminRoute>
              <AdminLayout>
                <AdminTenantDetail />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute>
              <AdminLayout>
                <AdminUsers />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/projects" element={
            <AdminRoute>
              <AdminLayout>
                <AdminProjects />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/subscriptions" element={
            <AdminRoute>
              <AdminLayout>
                <AdminSubscriptions />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/ai-usage" element={
            <AdminRoute>
              <AdminLayout>
                <AdminAIUsage />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/website-analytics" element={
            <AdminRoute>
              <AdminLayout>
                <AdminWebsiteAnalytics />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/training-video" element={
            <AdminRoute>
              <AdminLayout>
                <AdminTrainingVideo />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/audit-logs" element={
            <AdminRoute>
              <AdminLayout>
                <AdminAuditLogs />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/error-logs" element={
            <AdminRoute>
              <AdminLayout>
                <AdminErrorLogs />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/email-templates" element={
            <AdminRoute>
              <AdminLayout>
                <AdminEmailTemplates />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/newsletters" element={
            <AdminRoute>
              <AdminLayout>
                <AdminNewsletters />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/messages" element={
            <AdminRoute>
              <AdminLayout>
                <AdminMessageCenter />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/support" element={
            <AdminRoute>
              <AdminLayout>
                <AdminSupportInbox />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/notifications" element={
            <AdminRoute>
              <AdminLayout>
                <AdminNotifications />
              </AdminLayout>
            </AdminRoute>
          } />
          <Route path="/admin/inactive-users" element={
            <AdminRoute>
              <AdminLayout>
                <AdminInactiveUsers />
              </AdminLayout>
            </AdminRoute>
          } />
          
          {/* Catch all */}
          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        <GlobalErrorModal />
        <Toaster position="top-right" />
      </BrowserRouter>
      </ConfirmProvider>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
